import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import {
  ApartmentParameters,
  Building,
  LivingApartment,
  ParamsForGettingApartments,
  ResidentalComplex,
  SaveApartmentParameters,
} from '@shared/types/living.types';
import { RepairOptionsGroupItem } from '@configurator/modules/repair-type-page/repair-type-page.types';
import { UserProfile } from '@shared/authentication/interfaces';
import { Catalogs, CityLiving, Order, OrderContainer, Project } from '@shared/states/order.state';
import { SettingsService } from './settings.service';
import { SendStatisticsService, StatisticsContextEnum } from '@api/send-statistics.service';
import { Option } from '@configurator/configurator.types';
import { UiResponse } from '@shared/types/api.types';
import { PromoCodeRes } from '@configurator/modules/promo-code-dialog/promo-code-dialog.types';
import { QuizNextStep, QuizStat, QuizStep } from 'app/quiz/quiz.types';

export interface ApartParamsResponse {
  data: Order['apart_param'];
  error: {
    error: ApartParamsError;
  };
}

// export interface ChangedRepairOptionsResponse {
//   amounts: ProjectCalculate;
//   options: Partial<RepairOption>[];
// }

export interface ChangedRepairOptionsResponse {
  options: ChangedRepairOptions;
  project: ChangedProjectPrice;
}

export type ChangedRepairOptions = Record<
  string,
  { values: Record<string, ChangedPrice> } | ChangedPrice
>;

export interface ChangedPrice {
  total: number;
  works: number;
  materials: number;
}

export interface ChangedProjectPrice {
  materialTotal: number;
  materials: number;
  materialsFinishing: number;
  total: number;
  works: number;
}

export interface ApartParamsError extends HttpErrorResponse {
  data: ApartParamsErrorData;
}

export interface ApartParamsErrorData {
  [key: keyof Order['apart_param']]: string;
}

@Injectable({ providedIn: 'root' })
export class ApiService {
  constructor(
    protected readonly http: HttpClient,
    protected readonly settingsService: SettingsService,
    protected statistics: SendStatisticsService,
  ) {}

  public getCitiesList(): Observable<CityLiving[]> {
    return this.http
      .get<{ data: CityLiving[] }>(`${this.settingsService.livingApi}init/city-list`)
      .pipe(map((result) => (result ? result.data : null)));
  }

  public getComplexesList(ids: number[]): Observable<ResidentalComplex[]> {
    const params = {
      cityIds: `${ids.join(',')}`,
      realityType: 'RESALE',
    };

    return this.http
      .get<{ data: ResidentalComplex[] }>(`${this.settingsService.livingApi}partner-api/complex`, {
        params,
        headers: new HttpHeaders({ origin: 'https://ugol.me/' }),
      })
      .pipe(
        catchError((err) => of(null)),
        map((result) => result?.data),
      );
  }

  public getApartment(id: string, cityIds: number[]): Observable<LivingApartment> {
    const body = {
      cityIds: cityIds,
      id: id,
    };
    return this.http
      .post<{ data: LivingApartment }>(
        `${this.settingsService.livingApi}apartment/extended-card`,
        body,
        {
          headers: new HttpHeaders({ origin: 'https://ugol.me/' }),
        },
      )
      .pipe(
        catchError((err) => of(null)),
        map((result) => result?.data),
      );
  }

  public getLivingMarkers(ids: number[]): Observable<Building[]> {
    const params = {
      cityIds: `${ids.join(',')}`,
    };
    return this.http
      .get<{ data: Building[] }>(`${this.settingsService.livingApi}partner-api/building`, {
        params,
        headers: new HttpHeaders({ origin: 'https://ugol.me/' }),
      })
      .pipe(
        catchError((err) => of(null)),
        map((result) => result.data),
      );
  }

  public getApartmentsListByComplex(
    id: string,
    payload: ParamsForGettingApartments,
  ): Observable<LivingApartment[]> {
    if (!payload.roomsCntForFilter) {
      return of(null);
    }

    const params: Record<string, string | string[] | number | number[]> = {};

    params['roomsCntForFilter'] = payload.roomsCntForFilter;

    if (payload.areaTotalFrom) {
      params['areaTotalFrom'] = payload.areaTotalFrom;
    }

    if (payload.areaTotalTo) {
      params['areaTotalTo'] = payload.areaTotalTo;
    }

    if (payload.planningIds) {
      params['planningIds'] = payload.planningIds;
    }

    return this.http
      .post<{ data: LivingApartment[] }>(
        `${this.settingsService.livingApi}partner-api/complex/${id}/apartment`,
        payload,
      )
      .pipe(
        catchError((err) => of(null)),
        map((result) => result?.data),
      );
  }

  public getApartmentsListByBuilding(
    id: number,
    payload: ParamsForGettingApartments,
  ): Observable<LivingApartment[]> {
    if (!payload.roomsCntForFilter) {
      return of(null);
    }

    const params: Record<string, string | string[] | number | number[]> = {};

    params['roomsCntForFilter'] = payload.roomsCntForFilter;

    if (payload.areaTotalFrom) {
      params['areaTotalFrom'] = payload.areaTotalFrom;
    }

    if (payload.areaTotalTo) {
      params['areaTotalTo'] = payload.areaTotalTo;
    }

    if (payload.planningIds) {
      params['planningIds'] = payload.planningIds;
    }

    return this.http
      .post<{ data: LivingApartment[] }>(
        `${this.settingsService.livingApi}partner-api/building/${id}/apartment`,
        payload,
      )
      .pipe(
        catchError((err) => of(null)),
        map((result) => result?.data),
      );
  }

  public getListApartmentLayout(id: number) {
    return this.http
      .post<{ data: [] }>(`${this.settingsService.livingApi}partner-api/complex/${id}/planning`, {
        observe: 'response',
      })
      .pipe(
        catchError((err) => of(null)),
        map((result) => result?.data),
      );
  }

  public getQuizStat(): Observable<QuizStat> {
    return this.http
      .get<QuizStat>(`${this.settingsService.v2}client/configurator/quiz/main/quiz`)
      .pipe(catchError((err) => of(null)));
  }

  public getQuiz(): Observable<QuizStep[]> {
    return this.http
      .get<QuizStep[]>(`${this.settingsService.v2}client/configurator/quiz/main`)
      .pipe(catchError((err) => of(null)));
  }

  public completeQuiz(hash?: string): Observable<{ success: boolean; processed: boolean }> {
    return this.http
      .post<{ success: boolean; processed: boolean }>(
        `${this.settingsService.v2}client/configurator/quiz/main/quiz-complete`,
        null,
        {
          headers: this.statistics.createContextHeaders(StatisticsContextEnum.QUIZ, hash),
        },
      )
      .pipe(catchError((err) => of(null)));
  }

  public saveQuizStep(
    stepId: number,
    options: Record<string, string | number>,
  ): Observable<QuizNextStep> {
    return this.http
      .post<QuizNextStep>(`${this.settingsService.v2}client/configurator/quiz/main/step-complete`, {
        stepId: stepId,
        options: options,
      })
      .pipe(catchError((err) => of(null)));
  }

  public getOrder(onlyProjects = false): Observable<OrderContainer> {
    return this.http
      .get<OrderContainer>(`${this.settingsService.v2}client/configurator/get-order`, {
        params: { onlyProjects },
      })
      .pipe(catchError((err) => of(null)));
  }

  public getAnonymousOrder(saveOrder: boolean): Observable<unknown> {
    return this.http
      .get<unknown>(
        `${this.settingsService.v2}client/configurator/get-anonymous-order?${saveOrder}`,
      )
      .pipe(catchError((err) => of(null)));
  }
  public getApartmentParameters(params: HttpParams): Observable<ApartmentParameters> {
    return this.http
      .get<ApartmentParameters>(`${this.settingsService.v2}client/configurator/get-front-params`, {
        params,
      })
      .pipe(catchError((err) => of(null)));
  }

  public sendApartmentSettings(
    params: SaveApartmentParameters,
    hash?: string,
  ): Observable<ApartParamsResponse> {
    return this.http
      .post<{ apart_param: Order['apart_param'] }>(
        `${this.settingsService.v2}client/configurator/save-params`,
        params,
        {
          headers: this.statistics.createContextHeaders(StatisticsContextEnum.FLAT_PARAMS, hash),
        },
      )
      .pipe(
        map((result) => {
          return { data: result.apart_param, error: null };
        }),
        catchError((err) => {
          return of({ data: null, error: err });
        }),
      );
  }

  public getCatalogList(): Observable<Catalogs> {
    return this.http
      .get<Catalogs>(`${this.settingsService.v2}client/configurator/get-catalog`)
      .pipe(catchError((err) => of(null)));
  }

  public updateRepairType(level: number): Observable<OrderContainer> {
    return this.http
      .post<OrderContainer>(`${this.settingsService.v2}client/configurator/save-repair-type`, {
        repairType: level,
      })
      .pipe(catchError((err) => of(null)));
  }

  public setActiveProject(projectId: number, hash?: string): Observable<Project> {
    return this.http
      .post<Project>(
        `${this.settingsService.v2}client/configurator/set-active-project`,
        {
          projectId: projectId,
        },
        {
          headers: this.statistics.createContextHeaders(
            StatisticsContextEnum.REPAIR_OPTIMIZATION_SCREEN,
            hash,
          ),
        },
      )
      .pipe(catchError((err) => of(null)));
  }

  public createProject(
    project: {
      name: string;
      repairTypeId: number;
      templateId: number;
    },
    hash?: string,
  ): Observable<Project> {
    return this.http
      .post<Project>(`${this.settingsService.v2}client/configurator/create-self-project`, project, {
        headers: this.statistics.createContextHeaders(
          StatisticsContextEnum.REPAIR_OPTIMIZATION_SCREEN,
          hash,
        ),
      })
      .pipe(catchError((err) => of(null)));
  }

  public cloneProject(
    projectId: number,
    hash?: string,
    ctx = StatisticsContextEnum.REPAIR_OPTIMIZATION_SCREEN,
  ): Observable<Project> {
    return this.http
      .post<Project>(
        `${this.settingsService.v2}client/configurator/clone-project`,
        {
          projectId: projectId,
        },
        {
          headers: this.statistics.createContextHeaders(ctx, hash),
        },
      )
      .pipe(catchError((err) => of(null)));
  }

  public deleteProject(projectId: number, hash?: string): Observable<any> {
    return this.http
      .delete(`${this.settingsService.v2}client/configurator/delete-project`, {
        params: {
          projectId,
        },
        headers: this.statistics.createContextHeaders(
          StatisticsContextEnum.REPAIR_OPTIMIZATION_SCREEN,
          hash,
        ),
      })
      .pipe(catchError((err) => of(null)));
  }

  public updateMaterialLevel(level: number): Observable<boolean> {
    return this.http
      .post<boolean>(`${this.settingsService.v2}client/configurator/save-material-level`, {
        materialLevel: level,
      })
      .pipe(catchError((err) => of(false)));
  }

  public getMainOptions(): Observable<UiResponse<Option[]>> {
    return this.http
      .get<Option[]>(`${this.settingsService.v2}client/configurator/option/get-main`)
      .pipe(
        map((result) => {
          return { error: null, data: result };
        }),
        catchError((err) => of({ error: err, data: null })),
      );
  }

  public getRepairOptions(hash?: string): Observable<RepairOptionsGroupItem[]> {
    return this.http
      .get<RepairOptionsGroupItem[]>(`${this.settingsService.v2}client/configurator/get-options`, {
        headers: this.statistics.createContextHeaders(StatisticsContextEnum.MAIN_BOARD, hash),
      })
      .pipe(catchError((err) => of(null)));
  }

  public updateRepairTypeOptions(options: unknown, hash?: string): Observable<Project> {
    return this.http
      .post<Project>(`${this.settingsService.v2}client/configurator/save-options`, options, {
        headers: this.statistics.createContextHeaders(
          StatisticsContextEnum.REPAIR_OPTIMIZATION_SCREEN,
          hash,
        ),
      })
      .pipe(catchError((err) => of(null)));
  }

  public changeRepairOptions(data: {
    options: { [key: string]: string | number };
    groupId: number;
  }): Observable<ChangedRepairOptionsResponse> {
    return this.http
      .post<ChangedRepairOptionsResponse>(
        `${this.settingsService.v2}client/configurator/option/change-options`,
        data,
      )
      .pipe(catchError((err) => of(null)));
  }

  public resetChanges(): Observable<boolean> {
    return this.http
      .post<{ success: boolean }>(
        `${this.settingsService.v2}client/configurator/option/rest-change`,
        null,
      )
      .pipe(
        map(() => true),
        catchError((err) => of(false)),
      );
  }

  public resetParameters(): Observable<boolean> {
    return this.http
      .get<boolean>(`${this.settingsService.v2}client/configurator/reset-params`)
      .pipe(catchError((err) => of(false)));
  }

  public resetOptions(): Observable<boolean> {
    return this.http
      .get<boolean>(`${this.settingsService.v2}client/configurator/reset-options`)
      .pipe(catchError((err) => of(false)));
  }

  public resetOrder(hash?: string): Observable<boolean> {
    return this.http
      .get<boolean>(`${this.settingsService.v2}client/configurator/clear-order`, {
        headers: this.statistics.createContextHeaders(
          StatisticsContextEnum.MAIN_BOARD_CONTEXT_MENU,
          hash,
        ),
      })
      .pipe(catchError((err) => of(false)));
  }

  public getProfile(): Observable<UserProfile> {
    return this.http
      .get<UserProfile>(`${this.settingsService.v2}client/configurator/get-profile`)
      .pipe(catchError((err) => of(null)));
  }

  public updateProfile(formData: FormData): Observable<UserProfile> {
    return this.http
      .post<UserProfile>(`${this.settingsService.v2}client/configurator/update-profile`, formData)
      .pipe(catchError((err) => of(null)));
  }

  public deleteAvatar(): Observable<UserProfile> {
    return this.http.get<UserProfile>(
      `${this.settingsService.v2}client/configurator/update-profile-delete-avatar`,
    );
  }

  public downloadEstimate(hash?: string): Observable<Blob> {
    return this.http.get(`${this.settingsService.v2}client/configurator/upload-estimate-to-excel`, {
      responseType: 'blob',
      headers: this.statistics.createContextHeaders(
        StatisticsContextEnum.MAIN_BOARD_DOWNLOAD,
        hash,
      ),
    });
  }

  public downloadFiles(
    url: string,
    hash?: { context: StatisticsContextEnum; hash: string },
  ): Observable<Blob> {
    if (hash) {
      return this.http.get(url, {
        responseType: 'blob',
        headers: this.statistics.createContextHeaders(hash.context, hash.hash),
      });
    }

    return this.http.get(url, {
      responseType: 'blob',
    });
  }

  public checkPromo(promoCode: string): Observable<PromoCodeRes> {
    return this.http
      .get<PromoCodeRes>(
        `${this.settingsService.v2}client/configurator/payment/widget/promo-code-validate`,
        {
          params: {
            promoCode,
          },
        },
      )
      .pipe(catchError((err) => of(null)));
  }
}
